import { useCallback, useEffect, useState } from 'react';
import { bridgeApi, hasResolvedBand, type DepositLimits } from '../services/api';

/** How often the band is re-read. Caps are on-chain state and do change. */
const REFRESH_MS = 60_000;

export interface DepositLimitsState {
  limits: DepositLimits | null;
  loading: boolean;
  /**
   * Reject an amount the bridge would refuse, before the user signs. Returns
   * the reason, or null if the amount is fine (or if the band is unknown,
   * in which case the API is the one that says no).
   */
  validate: (amount: number) => string | null;
}

/**
 * The live deposit band for one token, read from GET /api/v1/deposit/limits.
 *
 * Nothing here is hardcoded on purpose. `maxDeposit` is the smallest cap
 * across every layer a deposit must clear, an operator can change it on-chain
 * at any time, and a UI that assumed a number would start advertising the
 * wrong one the moment they did.
 */
export function useDepositLimits(splMint?: string): DepositLimitsState {
  const [limits, setLimits] = useState<DepositLimits | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const fetchLimits = async () => {
      try {
        const data = await bridgeApi.getDepositLimits(splMint);
        if (!cancelled) setLimits(data);
      } catch {
        // No band beats a wrong band: the form shows nothing and the API
        // still enforces the real limits on submit.
        if (!cancelled) setLimits(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    setLoading(true);
    fetchLimits();
    const interval = setInterval(fetchLimits, REFRESH_MS);

    return () => { cancelled = true; clearInterval(interval); };
  }, [splMint]);

  const validate = useCallback(
    (amount: number): string | null => {
      // No band, or a band the API admits it invented, is not something to
      // hold a user to. The API still enforces the real caps on submit.
      if (!hasResolvedBand(limits)) return null;

      const symbol = limits.token ?? '';
      const min = parseFloat(limits.minDeposit);
      const max = parseFloat(limits.maxDeposit);

      if (Number.isFinite(min) && amount < min) {
        return `Minimum deposit is ${limits.minDeposit} ${symbol}`.trim();
      }
      if (Number.isFinite(max) && amount > max) {
        return `Maximum deposit is ${limits.maxDeposit} ${symbol}`.trim();
      }
      return null;
    },
    [limits],
  );

  return { limits, loading, validate };
}
